// PRD.326 — printing a patient form.
//
// WHY NOT THE EXISTING PRINT HELPER. `printHtmlViaIframe` takes a static HTML
// STRING built with `renderToStaticMarkup`. That cannot work here: PRD.324's
// pagination measures real rendered line boxes and page geometry in the DOM, so
// a string with no layout would produce an unpaginated document — the exact bug
// PRD.324 was written to fix. Printing therefore has to use the LIVE canonical
// render, not a re-serialization of it.
//
// SAME SESSION, HIDDEN IFRAME — the pattern prescription and invoice printing
// already use. Nothing navigates, nothing reloads, the host page is never
// altered, and cancelling leaves the user exactly where they were.
//
// `FormPrintMount` owns the iframe. This module holds only the pieces that are
// useful on their own: the print target shape and the readiness gate.
//
// Reusing `FormRendererPage` via `overrideIds` means the print surface inherits
// its data loading, its authorization and PRD.324's canonical paginated
// document. There is no second renderer and no duplicated pagination.

/** Which form instance to print. */
export interface FormPrintTarget {
  patientId: string;
  encounterId: string;
  formInstanceId: string;
}

/**
 * Resolve once the canonical document is genuinely ready to print.
 *
 * The naive version — check images once, then print — fires while the page is
 * still showing "Loading…", because at that instant PowerSync has not returned
 * and there are no sheets and no images to wait for. "Nothing to wait for" and
 * "ready" are not the same thing.
 *
 * So this POLLS for a settled document rather than sampling once:
 *
 *   1. at least one `.render-page` exists  → data has rendered
 *   2. every `<img>` inside it is complete → template artwork has decoded
 *   3. the state holds for two consecutive frames → layout has stopped moving
 *
 * Web fonts are awaited first, since they change wrapping and therefore
 * pagination. The timeout is a last-resort escape hatch so a broken image URL
 * cannot leave the user staring at a dialog that never opens — it is not the
 * synchronization mechanism.
 */
export async function waitForPrintReady(
  root: () => Element | Document | null,
  timeoutMs = 15000,
): Promise<void> {
  const deadline = Date.now() + timeoutMs;

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    await Promise.race([document.fonts.ready, delay(timeoutMs)]);
  }

  let stableFrames = 0;
  while (Date.now() < deadline) {
    if (isDocumentSettled(root())) {
      stableFrames += 1;
      // Two consecutive settled frames: React has committed and nothing new
      // has started loading.
      if (stableFrames >= 2) return;
    } else {
      stableFrames = 0;
    }
    await nextFrame();
  }
}

/**
 * Whether the print document currently looks complete.
 *
 * Deliberately requires a sheet to EXIST. An empty container satisfies "all
 * images loaded" trivially, which is exactly how the loading state slipped
 * through before.
 */
function isDocumentSettled(container: Element | Document | null): boolean {
  if (!container || typeof container.querySelectorAll !== 'function') return false;

  const pages = container.querySelectorAll('.render-page');
  if (pages.length === 0) return false;

  const images = Array.from(container.querySelectorAll('img'));
  return images.every((img) => img.complete);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 16);
  });
}
